import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomePerson } from '../queries/some-people.query';

import { useFindAllDeletedExternalPeopleQuery } from './useFindAllDeletedExternalPeopleQuery.hook';
import { useFindAllDeletedInternalPeopleQuery } from './useFindAllDeletedInternalPeopleQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{ findSomePeople: readonly ThirdPartiesCmp_SomePerson[] }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllDeletedPeopleQuery = (enabled = true): UnionResult => {
  const internalQuery = useFindAllDeletedInternalPeopleQuery(enabled);
  const externalQuery = useFindAllDeletedExternalPeopleQuery(enabled);

  const memoizedResult = useMemo(() => {
    const internalData = internalQuery.data?.findSomePeople;
    const externalData = externalQuery.data?.findSomePeople;

    const data =
      internalData || externalData
        ? {
            findSomePeople: [
              ...(internalData ?? EMPTY_ARRAY),
              ...(externalData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading = internalQuery.isLoading || externalQuery.isLoading;
    const isSuccess = internalQuery.isSuccess && externalQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    internalQuery.data?.findSomePeople,
    internalQuery.isLoading,
    internalQuery.isSuccess,
    externalQuery.data?.findSomePeople,
    externalQuery.isLoading,
    externalQuery.isSuccess,
  ]);

  return memoizedResult;
};
